import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Boxes, MessageSquareQuote, Tv2, Users, ArrowUpRight } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";

const STATS = [
  { key: "total_products", label: "Products", icon: Boxes, link: "/dashboard/catalog" },
  { key: "total_questions", label: "Questions", icon: MessageSquareQuote, link: "/dashboard/questions" },
  { key: "total_experiences", label: "Experiences", icon: Tv2, link: "/dashboard/experiences" },
  { key: "total_sessions", label: "Sessions", icon: Users, link: "/dashboard/analytics" },
];

export default function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/analytics/overview").then((r) => setData(r.data));
  }, []);

  return (
    <>
      <PageHeader
        eyebrow="Dashboard"
        title="Overview"
        description="Snapshot of your brand experience program."
      >
        <Link to="/dashboard/experiences">
          <Button className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5" data-testid="overview-create-link">
            Create kiosk link <ArrowUpRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </PageHeader>

      <div className="p-10 space-y-10">
        {/* Stat grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#e5e5e5] border border-[#e5e5e5]">
          {STATS.map(({ key, label, icon: Icon, link }) => (
            <Link
              key={key}
              to={link}
              data-testid={`stat-${key}`}
              className="bg-white p-6 hover:bg-[#fafafa] transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="label-overline">{label}</span>
                <Icon className="w-4 h-4 text-[#52525b]" />
              </div>
              <div className="mt-6 font-display font-black tracking-tighter text-5xl">
                {data ? data[key] : "—"}
              </div>
            </Link>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Visits chart */}
          <div className="card-brutalist lg:col-span-2 bg-white border border-[#e5e5e5] p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-overline">Visits</div>
                <div className="mt-2 font-display font-bold text-xl tracking-tight">Last 14 days</div>
              </div>
            </div>
            <div className="h-64 mt-6">
              {data && data.visits_series && data.visits_series.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.visits_series}>
                    <XAxis dataKey="date" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #e5e5e5", fontFamily: "IBM Plex Sans" }} />
                    <Line type="monotone" dataKey="visits" stroke="#0F1210" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-[#52525b] border border-dashed border-[#e5e5e5]">
                  No visits yet — share your kiosk link to start collecting data
                </div>
              )}
            </div>
          </div>

          {/* Top products */}
          <div className="card-brutalist bg-white border border-[#e5e5e5] p-6">
            <div className="label-overline">Top recommended</div>
            <div className="mt-2 font-display font-bold text-xl tracking-tight">Products</div>
            <ul className="mt-6 space-y-3">
              {data && data.top_products && data.top_products.length > 0 ? (
                data.top_products.map((p) => (
                  <li key={p.id} className="flex items-center justify-between py-2 border-b border-[#e5e5e5] last:border-b-0">
                    <span className="text-sm font-medium truncate">{p.title}</span>
                    <span className="font-mono text-xs text-[#52525b]">×{p.count}</span>
                  </li>
                ))
              ) : (
                <li className="text-sm text-[#52525b]">No sessions yet.</li>
              )}
            </ul>
          </div>
        </div>

        {/* Quick start checklist */}
        <div className="card-brutalist bg-white border border-[#e5e5e5] p-8">
          <div className="label-overline">Quick start</div>
          <div className="mt-2 font-display font-bold text-2xl tracking-tight">Launch your first experience</div>
          <ol className="mt-6 grid md:grid-cols-2 gap-px bg-[#e5e5e5]">
            {[
              ["01", "Fill out your brand info", "/dashboard/brand"],
              ["02", "Add products to your catalog", "/dashboard/catalog"],
              ["03", "Author preference questions", "/dashboard/questions"],
              ["04", "Generate your kiosk link", "/dashboard/experiences"],
            ].map(([n, label, link]) => (
              <Link key={n} to={link} className="bg-white p-6 hover:bg-[#fafafa] transition-colors flex items-center justify-between" data-testid={`quickstart-${n}`}>
                <div>
                  <div className="label-overline">Step {n}</div>
                  <div className="mt-2 font-medium">{label}</div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-[#0F1210]" />
              </Link>
            ))}
          </ol>
        </div>
      </div>
    </>
  );
}
